import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine } from "lucide-react";

/**
 * Simple canvas signature pad. Exposes `getDataUrl()` and `isEmpty()` via ref.
 */
const SignaturePad = React.forwardRef(({ height = 180 }, ref) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    // Match backing store to CSS size for crisp lines on retina
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);

  React.useImperativeHandle(ref, () => ({
    getDataUrl: () => (isEmpty ? null : canvasRef.current.toDataURL("image/png")),
    clear: () => clear(),
    isEmpty: () => isEmpty,
  }));

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
    setIsEmpty(true);
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><PenLine className="h-3 w-3" /> {t("auto.firma_cliente")}</span>
        <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="sig-clear"><Eraser className="mr-1 h-3 w-3" /> {t("auto.cancella_btn")}</Button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="signature-pad"
        style={{ height, width: "100%", touchAction: "none" }}
        className="rounded-sm border-2 border-dashed border-slate-300 bg-white"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
    </div>
  );
});
SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
